import React, { useState, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { useTypedTranslation } from '@app/hooks';

import { ModalActionsComponent } from '@app/components';
import { getModalSwitch, getLoaderBtn, setExceptionHandler } from '@app/store/actions';
import { basicService, jitUsersService } from '@app/services';
import { IJitUserNextSlotAvailable } from '@app/models';
import { passlyFormatDate, DATE_TIME_FORMAT, passlyHumanizeDate, onPressEnterModal, genericErrorString } from '@app/helpers';

export interface IReceivedData {
    id: string;
    canReserve: boolean;
    handleRefresh: Function;
}

const ReserveUserDialog = (props: IReceivedData) => {
    const dispatch = useDispatch();

    const { translation } = useTypedTranslation();

    const [errorMessage, setErrorMessage] = useState<string | undefined>(undefined);
    const [nextSlotAvailable, setNextSlotAvailable] = useState<IJitUserNextSlotAvailable | undefined>(undefined);
    const [loading, setLoading] = useState<boolean>(true);

    useEffect(() => {
        dispatch(setExceptionHandler(true));

        if (props.canReserve) {
            jitUsersService.getTimeSlot(props.id, handleError, handleNextSlotAvailableData);
        }
        else {
            setLoading(false);
        }
    }, []);

    const handleNextSlotAvailableData = (response: any) => {
        const nextSlot: IJitUserNextSlotAvailable = response.data;
        setLoading(false);
        setNextSlotAvailable(nextSlot);
    };

    const handleError = (error: any) => {
        setErrorMessage(genericErrorString(error));
        setLoading(false);
        dispatch(getLoaderBtn(false));
    };

    const handleCancel = () => {
        dispatch(getModalSwitch(false));
    };

    const handleSuccess = () => {
        dispatch(getLoaderBtn(false));
        dispatch(getModalSwitch(false));
        props.handleRefresh(true);
    };

    const handleReserveBtn = () => {
        if (props.canReserve) {
            jitUsersService.reserveUserSlot(props.id, handleError, handleSuccess);
            dispatch(getLoaderBtn(true));
        }
        else {
            basicService.redirect('/profile?registermfa=true');
        }
    };

    const handleOKButton = () => {
        if (props.canReserve) {
            return translation('JIT_RESERVE_OK');
        }
        else {
            return translation('JIT_NAV_TO_MFA_REG');
        }
    };

    return (
        <>
        <div className='modal__form' tabIndex={0} onKeyPress={onPressEnterModal(handleReserveBtn)}>
            <div className='modal__large flex1'>
                <div className='modal__body--row jitUsers'>
                    <p className='jitUsers__reserve'>{translation('JIT_RESERVE_DESC')}</p>
                    {props.canReserve && nextSlotAvailable ?
                        <div className='jitUsers__reserve'>
                            {nextSlotAvailable.AlreadyQueued > 0 &&
                                <span>
                                    <div className='jitUsers__reserve-box'>{translation('JIT_RESERVE_QUEUE_LONG')}</div>
                                    <div className='jitUsers__reserve-box'>{translation('JIT_QUEUE_LENGTH')}: {nextSlotAvailable.AlreadyQueued}</div>
                                </span>
                            }

                            {nextSlotAvailable.AlreadyReserved &&
                                <span>
                                    <div className='jitUsers__reserve-box'>{translation('JIT_ALREADY_QUEUED_DESC')}</div>
                                </span>
                            }

                            <h4 className='jitUsers__reserve-header'>{translation('JIT_RESERVATION_HEADER')}</h4>
                            <div className='jitUsers__reserve-box'>{translation('JIT_QUEUE_START')}: {passlyFormatDate(nextSlotAvailable.Start, DATE_TIME_FORMAT)}</div>
                            <div className='jitUsers__reserve-box'>{translation('JIT_QUEUE_WINDOW')}: {passlyHumanizeDate(nextSlotAvailable.Window)}</div>
                        </div>
                        :
                        <div>
                            {!loading &&
                                <span className='jitUsers__reserve jitUsers__reserve--warning'>{translation('JIT_CANNOT_RESERVE_NO_MFA')}</span>
                            }
                        </div>
                    }
                </div>
            </div>
            {
                errorMessage &&
                <div className='modal__error'>{errorMessage}</div>
            }
        </div>
            <ModalActionsComponent
                primaryBtn={handleOKButton()}
                secondaryBtn={translation('CANCEL')}
                onClickPrimary={handleReserveBtn}
                onClickSecondary={handleCancel}
                disabled={false}
                id='reserveUser'
            />
        </>
    );
};

export default ReserveUserDialog;
